import {
  AtSign,
  Bell,
  BriefcaseBusiness,
  CircleAlert,
  CircleCheck,
  Clock3,
  CreditCard,
  Info,
  MessageCircle,
  Package,
  PlugZap,
  ShieldAlert,
  UserRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { sanitizeNotificationHtml } from "@/lib/notifications/notificationContent";
import { Badge } from "@/components/ui/badge";
import { Close, Done, Hint } from "@/components/common/Icons/Icons";
import { useTranslation } from "react-i18next";

const severityConfig = {
  error: {
    accent: "bg-commerce-red",
    icon: CircleAlert,
    iconClassName: "text-commerce-red",
    iconBackground: "bg-commerce-red/10",
  },
  warning: {
    accent: "bg-amber-500",
    icon: CircleAlert,
    iconClassName: "text-amber-600 dark:text-amber-400",
    iconBackground: "bg-amber-500/10",
  },
  success: {
    accent: "bg-commerce-green",
    icon: CircleCheck,
    iconClassName: "text-commerce-green",
    iconBackground: "bg-commerce-green/10",
  },
  info: {
    accent: "bg-commerce-primary",
    icon: Info,
    iconClassName: "text-commerce-primary",
    iconBackground: "bg-commerce-primary/10",
  },
};

const categoryIcons = {
  system: Bell,
  hint: Hint,
  mention: AtSign,
  reminder: Clock3,
  integration: PlugZap,
  order: Package,
  customer: UserRound,
  case: BriefcaseBusiness,
  chat: MessageCircle,
  security: ShieldAlert,
  billing: CreditCard,
  other: Bell,
};

function normalizeSeverity(severity) {
  return severityConfig[severity] ? severity : "info";
}

export function RichNotificationBody({ notification, className }) {
  const { t } = useTranslation();
  const rawContent = notification?.htmlBody || notification?.body;
  if (!rawContent) return null;

  const content = t(rawContent);

  return (
    <div
      className={cn(
        "min-w-0 max-w-full whitespace-pre-wrap break-words text-sm leading-5 text-commerce-07 [overflow-wrap:anywhere]",
        "[&_a]:break-all [&_a]:font-normal [&_a]:text-commerce-primary [&_a]:underline [&_a]:underline-offset-2",
        "[&_li]:ml-4 [&_ol]:my-1 [&_ol]:list-decimal [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ul]:list-disc",
        "[&_strong]:font-bold [&_*]:max-w-full",
        className,
      )}
      dangerouslySetInnerHTML={{
        __html: sanitizeNotificationHtml(content),
      }}
    />
  );
}

export function NotificationGlyph({
  severity = "info",
  category,
  className,
}) {
  const normalizedSeverity = normalizeSeverity(severity);
  const config = severityConfig[normalizedSeverity];
  const Icon = categoryIcons[category] || config.icon;

  return (
    <span
      className={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-md",
        config.iconBackground,
        config.iconClassName,
        className,
      )}
      aria-hidden="true"
    >
      <Icon className="size-[18px]" strokeWidth={2} />
    </span>
  );
}

export function NotificationSurface({
  severity = "info",
  category,
  title,
  body,
  source,
  meta,
  unread = false,
  closeLabel,
  onClose,
  children,
  className,
  role = "status",
  verticallyCentered = false,
}) {
  const normalizedSeverity = normalizeSeverity(severity);

  return (
    <div
      role={role}
      className={cn(
        "relative flex min-w-0 overflow-hidden rounded-lg border border-commerce-03 bg-commerce-02 text-left text-commerce-09 shadow-[0_12px_32px_rgba(0,0,0,0.16)]",
        className,
      )}
    >
      <span
        className={cn(
          "absolute inset-y-0 left-0 w-1",
          severityConfig[normalizedSeverity].accent,
        )}
        aria-hidden="true"
      />

      <div
        className={cn(
          "flex min-w-0 flex-1 gap-3 py-3 pl-4 pr-3",
          verticallyCentered ? "items-center" : "items-start",
        )}
      >
        <NotificationGlyph severity={normalizedSeverity} category={category} />

        <div className="min-w-0 flex-1 text-left">
          <div className="flex min-w-0 items-start gap-2">
            <div className="min-w-0 flex-1">
              {title && (
                <div
                  className={cn(
                    "min-w-0 break-words text-sm leading-5 [overflow-wrap:anywhere]",
                    unread ? "font-bold" : "font-semibold",
                  )}
                >
                  {title}
                </div>
              )}
              {body && (
                <div
                  className={cn(
                    "min-w-0 break-words text-sm leading-5 text-commerce-07 [overflow-wrap:anywhere]",
                    title && "mt-0.5",
                  )}
                >
                  {body}
                </div>
              )}
            </div>

            {unread && (
              <span
                className="mt-1.5 size-2 shrink-0 rounded-full bg-commerce-primary"
                aria-hidden="true"
              />
            )}
          </div>

          {(source || meta) && (
            <div className="-ml-1.5 mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-4 text-commerce-06">
              {source && (
                <Badge
                  variant="secondary"
                  className="max-w-40 truncate border-transparent bg-commerce-03 px-1.5 py-0 font-semibold capitalize text-commerce-07"
                >
                  {source}
                </Badge>
              )}
              {source && meta && (
                <span className="size-1 rounded-full bg-commerce-04" />
              )}
              {meta && <span className="min-w-0 truncate">{meta}</span>}
            </div>
          )}

          {children && (
            <div className="-ml-2 mt-2 flex min-w-0 flex-wrap items-center gap-2">
              {children}
            </div>
          )}
        </div>

        {onClose && (
          <button
            type="button"
            className={cn(
              "-mr-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-commerce-06 transition-colors hover:bg-commerce-03 hover:text-commerce-09 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              !verticallyCentered && "-mt-1",
            )}
            aria-label={closeLabel}
            title={closeLabel}
            onClick={onClose}
          >
            <Close className="size-4 text-current" />
          </button>
        )}
      </div>
    </div>
  );
}

export function NotificationActionButton({
  icon: Icon = Done,
  children,
  className,
  ...props
}) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex min-h-7 min-w-0 cursor-pointer items-center gap-1.5 rounded-md px-2 text-left text-xs font-bold text-commerce-08 transition-colors hover:bg-commerce-03 hover:text-commerce-09 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      {...props}
    >
      <Icon className="size-3.5" />
      <span className="truncate">{children}</span>
    </button>
  );
}
